import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, TextField, InputAdornment, Button, Grid, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { parseISO, isValid, format, parse } from 'date-fns';
import GoogleAutocomplete from 'src/components/AddressAutocomplete';
import { Iconify } from 'src/components';

/**
 * Parse Google Places address_components into structured address fields
 */
const parseAddressComponents = (addressComponents, formattedAddress) => {
  const result = {
    address_line_1: '',
    address_line_2: '',
    city: '',
    county: '',
    postcode: '',
  };

  if (!addressComponents || addressComponents.length === 0) return result;

  const address1 = formattedAddress ? formattedAddress.split(',')[0] : '';
  result.address_line_1 = address1;

  addressComponents.forEach((component) => {
    if (component.types.indexOf('postal_code') > -1) {
      result.postcode = component.long_name;
    }
    if (component.types.indexOf('country') > -1) {
      if (address1 === component.long_name) {
        result.address_line_1 = '';
      }
      result.county = component.long_name;
    }
    if (component.types.indexOf('locality') > -1 || component.types.indexOf('postal_town') > -1) {
      if (address1.includes(component.long_name)) {
        result.address_line_1 = '';
      }
      result.city = component.long_name;
    }
    if (component.types.indexOf('route') > -1) {
      if (!address1.includes(component.long_name)) {
        result.address_line_2 = component.long_name;
      }
    }
  });

  return result;
};

/**
 * CaptureContactDetailsLive - Functional component for capturing phone, email,
 * address (Google lookup), and "How did you hear about us?" (referral icon grid)
 */
const CaptureContactDetailsLive = ({ formData, onChange, referralOptions, disabled }) => {
  const theme = useTheme();
  const [addressExpanded, setAddressExpanded] = useState(false);

  // Determine if address fields should be shown
  const hasAddressData = useMemo(() => (
    formData.address_line_1 || formData.address_line_2 || formData.city || formData.county || formData.postcode
  ), [formData.address_line_1, formData.address_line_2, formData.city, formData.county, formData.postcode]);

  const showAddressFields = addressExpanded || hasAddressData;

  // Build effective referral options - include saved value if not in predefined options
  const effectiveReferralOptions = useMemo(() => {
    const baseOptions = referralOptions || [];
    const savedReferral = formData.referral;

    // If there's a saved referral value that's not in the options, add it
    if (savedReferral && !baseOptions.some(opt => opt.value === savedReferral)) {
      // Create a display-friendly label from the value (e.g., "CINEMA" -> "Cinema")
      const label = savedReferral.charAt(0).toUpperCase() + savedReferral.slice(1).toLowerCase();
      return [
        ...baseOptions,
        {
          value: savedReferral,
          label,
          icon: 'mdi:source-branch', // Generic icon for custom referrals
        }
      ];
    }

    return baseOptions;
  }, [referralOptions, formData.referral]);

  // Handle Google Place selection
  const handlePlaceSelected = useCallback((place, inputRef) => {
    const addressComponents = place.address_components;
    const fullAddress = inputRef?.value || place.formatted_address || '';

    const parsed = parseAddressComponents(addressComponents, fullAddress);

    onChange('address_line_1', parsed.address_line_1);
    onChange('address_line_2', parsed.address_line_2);
    onChange('city', parsed.city);
    onChange('county', parsed.county);
    onChange('postcode', parsed.postcode);
    setAddressExpanded(true);
  }, [onChange]);

  // Handle referral selection
  const handleReferralSelect = useCallback((value) => {
    if (disabled) return;
    const newValue = formData.referral === value ? '' : value;
    onChange('referral', newValue);
  }, [formData.referral, onChange, disabled]);

  // Styles
  const fieldLabelSx = {
    fontSize: '0.8rem',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    mb: 0.5,
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      backgroundColor: theme.palette.background.paper,
      fontSize: '0.875rem',
      '& fieldset': {
        borderColor: theme.palette.divider,
      },
    },
    '& .MuiInputBase-input': {
      py: 1.25,
      px: 1.5,
    },
  };

  const addressInputContainerSx = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '10px',
    backgroundColor: theme.palette.background.paper,
    overflow: 'hidden',
    '&:focus-within': {
      borderColor: theme.palette.primary.main,
    },
  };

  const referralCardSx = (isSelected) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.75,
    p: 1.5,
    borderRadius: '12px',
    border: `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
    backgroundColor: isSelected ? `${theme.palette.primary.main}08` : theme.palette.background.paper,
    cursor: disabled ? 'default' : 'pointer',
    transition: 'all 0.15s ease',
    minHeight: 80,
    '&:hover': disabled ? {} : {
      borderColor: theme.palette.primary.main,
      backgroundColor: `${theme.palette.primary.main}04`,
    },
  });

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Phone + Email + DOB Row */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
        {/* Phone Number */}
        <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 0 } }}>
          <Typography sx={fieldLabelSx}>
            <Iconify icon="solar:phone-bold" sx={{ width: 14, height: 14 }} />
            Phone number
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="07700 900123"
            value={formData.phone_number || ''}
            onChange={(e) => onChange('phone_number', e.target.value)}
            disabled={disabled}
            sx={textFieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="twemoji:flag-united-kingdom" sx={{ width: 18, height: 18, mr: -0.5 }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Email Address */}
        <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 0 } }}>
          <Typography sx={fieldLabelSx}>
            <Iconify icon="solar:letter-bold" sx={{ width: 14, height: 14 }} />
            Email address
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="name@example.com"
            value={formData.email || ''}
            onChange={(e) => onChange('email', e.target.value)}
            disabled={disabled}
            sx={textFieldSx}
          />
        </Box>

        {/* Date of Birth */}
        <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 0 } }}>
          <Typography sx={fieldLabelSx}>
            <Iconify icon="solar:calendar-bold" sx={{ width: 14, height: 14 }} />
            Date of birth
            <Typography component="span" sx={{ fontSize: '0.7rem', color: theme.palette.text.disabled, ml: 0.5 }}>
              (optional)
            </Typography>
          </Typography>
          <MobileDatePicker
            value={(() => {
              // Properly parse the date value - check for empty/invalid values
              const val = formData.date_of_birth;
              if (!val || val === '' || val === 'null' || val === null || val === undefined) {
                return null;
              }
              // Try parsing ISO format first (YYYY-MM-DD)
              let parsed = parseISO(val);
              if (!isValid(parsed) || parsed.getFullYear() <= 1900) {
                // Try parsing DD-MM-YYYY format (common API format)
                parsed = parse(val, 'dd-MM-yyyy', new Date());
              }
              if (!isValid(parsed) || parsed.getFullYear() <= 1900) {
                // Try parsing DD/MM/YYYY format
                parsed = parse(val, 'dd/MM/yyyy', new Date());
              }
              return isValid(parsed) && parsed.getFullYear() > 1900 ? parsed : null;
            })()}
            onChange={(newValue) => {
              let formattedValue = '';
              if (newValue && isValid(newValue)) {
                // Format as YYYY-MM-DD string for API
                formattedValue = format(newValue, 'yyyy-MM-dd');
              }
              onChange('date_of_birth', formattedValue);
            }}
            format="dd/MM/yyyy"
            maxDate={new Date()}
            disabled={disabled}
            slotProps={{
              textField: {
                fullWidth: true,
                size: 'small',
                placeholder: 'DD/MM/YYYY',
                sx: textFieldSx,
                InputProps: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" edge="end" disabled={disabled}>
                        <Iconify icon="solar:calendar-mark-bold-duotone" sx={{ width: 20, height: 20, color: '#637381' }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              },
            }}
          />
        </Box>
      </Box>

      {/* Home Address */}
      <Box>
        <Typography sx={fieldLabelSx}>
          <Iconify icon="solar:home-2-bold" sx={{ width: 14, height: 14 }} />
          Home address
          <Typography component="span" sx={{ fontSize: '0.7rem', color: theme.palette.text.disabled, ml: 0.5 }}>
            (optional)
          </Typography>
        </Typography>
        <Box sx={addressInputContainerSx}>
          <GoogleAutocomplete
            onPlaceSelected={handlePlaceSelected}
            disabled={disabled}
            style={{
              width: '100%',
              border: 'none',
              outline: 'none',
              padding: '10px 14px',
              fontSize: '0.875rem',
              backgroundColor: 'transparent',
              fontFamily: 'inherit',
            }}
            options={{
              types: ['establishment', 'geocode'],
              fields: ['address_components', 'formatted_address'],
              componentRestrictions: { country: 'uk' },
            }}
            defaultValue=""
            placeholder="Start typing postcode or address..."
          />
          <Button
            size="small"
            disabled={disabled}
            sx={{
              mr: 1,
              minWidth: 'auto',
              px: 1.5,
              py: 0.5,
              fontSize: '0.75rem',
              fontWeight: 500,
              color: theme.palette.text.secondary,
              borderRadius: '8px',
              border: `1px solid ${theme.palette.divider}`,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
            startIcon={<Iconify icon="eva:search-outline" sx={{ width: 14, height: 14 }} />}
            onClick={() => setAddressExpanded(true)}
          >
            Lookup
          </Button>
        </Box>

        {/* Expanded address fields */}
        {showAddressFields && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              label="Address line 1"
              value={formData.address_line_1 || ''}
              onChange={(e) => onChange('address_line_1', e.target.value)}
              disabled={disabled}
              sx={textFieldSx}
            />
            <TextField
              fullWidth
              size="small"
              label="Address line 2"
              value={formData.address_line_2 || ''}
              onChange={(e) => onChange('address_line_2', e.target.value)}
              disabled={disabled}
              sx={textFieldSx}
            />
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                label="City"
                value={formData.city || ''}
                onChange={(e) => onChange('city', e.target.value)}
                disabled={disabled}
                sx={textFieldSx}
              />
              <TextField
                fullWidth
                size="small"
                label="County"
                value={formData.county || ''}
                onChange={(e) => onChange('county', e.target.value)}
                disabled={disabled}
                sx={textFieldSx}
              />
              <TextField
                size="small"
                label="Postcode"
                value={formData.postcode || ''}
                onChange={(e) => onChange('postcode', e.target.value)}
                disabled={disabled}
                sx={{ ...textFieldSx, minWidth: 120 }}
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* How did you hear about us? */}
      <Box>
        <Typography sx={{ ...fieldLabelSx, mb: 1.5 }}>
          <Iconify icon="solar:magic-stick-3-bold" sx={{ width: 14, height: 14 }} />
          How did you hear about us?
        </Typography>
        <Grid container spacing={1}>
          {effectiveReferralOptions.map((option) => {
            const isSelected = formData.referral === option.value;
            return (
              <Grid item xs={3} key={option.value}>
                <Box
                  onClick={() => handleReferralSelect(option.value)}
                  sx={referralCardSx(isSelected)}
                >
                  {option.icon && (
                    <Iconify
                      icon={option.icon}
                      sx={{
                        width: 22,
                        height: 22,
                        color: isSelected ? theme.palette.primary.main : theme.palette.text.secondary,
                      }}
                    />
                  )}
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: isSelected ? 600 : 400,
                      color: isSelected ? theme.palette.primary.main : theme.palette.text.secondary,
                      textAlign: 'center',
                      lineHeight: 1.2,
                    }}
                  >
                    {option.label}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Box>
  );
};

CaptureContactDetailsLive.propTypes = {
  formData: PropTypes.object,
  onChange: PropTypes.func,
  referralOptions: PropTypes.array,
  disabled: PropTypes.bool,
};

CaptureContactDetailsLive.defaultProps = {
  formData: {},
  onChange: () => {},
  referralOptions: [],
  disabled: false,
};

export default CaptureContactDetailsLive;
